// Castintech dashboard — Acil Toplantı view (2026-05-27 MVP).
// ADR: wiki/decisions/2026-05-27-acil-toplanti-mekanizmasi-plan.md
// 4 tip: T1-board, T2-topic, T3-crisis, T4-async
// MVP: T2 + T3 öncelikli; 5sn poll (SSE V2'de).

import { apiFetch } from '../api.js';
import { state } from '../state.js';
import { escHtml, escAttr, $, getGem } from '../utils.js';
import { toast } from '../ui/toast.js';
import { createSkeleton, renderError } from '../ui/state-display.js';

interface Meeting {
  id: string;
  type: 'T1-board' | 'T2-topic' | 'T3-crisis' | 'T4-async';
  title: string;
  description?: string;
  initiated_by: string;
  target_duration_mins: number;
  scheduled_start: string;
  actual_start?: string;
  ended_at?: string;
  status: 'scheduled' | 'active' | 'voting' | 'completed' | 'cancelled';
  participant_count?: number;
  decision_count?: number;
}

const TYPE_LABEL: Record<string, string> = {
  'T1-board': '🏛️ Yönetim Kurulu',
  'T2-topic': '💬 Konu Toplantısı',
  'T3-crisis': '🔴 Kriz Toplantısı',
  'T4-async': '⏳ Asenkron Mini-Board',
};

const STATUS_LABEL: Record<string, string> = {
  scheduled: 'Planlı',
  active: 'Aktif',
  voting: 'Oylanıyor',
  completed: 'Tamamlandı',
  cancelled: 'İptal',
};

const STATUS_COLOR: Record<string, string> = {
  scheduled: '#94a3b8',
  active: '#22c55e',
  voting: '#f59e0b',
  completed: '#64748b',
  cancelled: '#ef4444',
};

let _meetings: Meeting[] = [];
let _lastError: string | null = null;
let _loaded = false;
let _pollTimer: number | null = null;

export async function loadMeetings(): Promise<void> {
  if (!_loaded && state.currentPage === 'meetings') {
    const root = $('meetings-content');
    if (root) {
      root.innerHTML = '';
      root.appendChild(createSkeleton({ rows: 4, height: '80px', rowGap: '10px' }));
    }
  }
  try {
    const r = await apiFetch('/castintech-api/meetings');
    if (!r.ok) throw new Error('HTTP ' + r.status);
    const d = await r.json() as { meetings: Meeting[]; count: number };
    _meetings = d.meetings || [];
    _lastError = null;
  } catch (e) {
    _lastError = (e as Error).message || 'Toplantı listesi alınamadı';
  }
  _loaded = true;
  if (state.currentPage === 'meetings') renderMeetings();
}

export function renderMeetings(): void {
  const root = $('meetings-content');
  if (!root) return;

  if (_lastError && _meetings.length === 0) {
    root.innerHTML = '';
    renderError(root, _lastError, {
      title: 'Toplantılar yüklenirken sorun oluştu',
      description: _lastError,
      retryLabel: 'Tekrar dene',
      onRetry: () => loadMeetings(),
    });
    return;
  }

  const active = _meetings.filter(m => m.status === 'active' || m.status === 'voting');
  const scheduled = _meetings.filter(m => m.status === 'scheduled');
  const completed = _meetings.filter(m => m.status === 'completed').slice(0, 5);

  root.innerHTML = `
    <div class="meetings-toolbar" style="padding:12px;display:flex;gap:10px;align-items:center;border-bottom:1px solid var(--border);background:var(--surface)">
      <button class="btn btn-primary" id="meetings-new-btn" style="padding:8px 16px;font-weight:600">+ Acil Toplantı Başlat</button>
      <span style="color:var(--muted);font-size:12px">Aktif: ${active.length} · Planlı: ${scheduled.length} · Son: ${completed.length}</span>
      <button class="btn btn-secondary" id="meetings-refresh-btn" style="margin-left:auto;padding:6px 12px;font-size:12px">↻ Yenile</button>
    </div>

    ${active.length > 0 ? `
    <div style="padding:16px;background:rgba(34,197,94,0.08);border-bottom:2px solid #22c55e">
      <div style="font-size:11px;letter-spacing:0.5px;color:#22c55e;font-weight:600;margin-bottom:8px">🔴 AKTİF TOPLANTI</div>
      ${active.map(renderMeetingCard).join('')}
    </div>
    ` : ''}

    ${scheduled.length > 0 ? `
    <div style="padding:16px;border-bottom:1px solid var(--border)">
      <div style="font-size:11px;letter-spacing:0.5px;color:#94a3b8;font-weight:600;margin-bottom:8px">📅 PLANLI (next 24h)</div>
      ${scheduled.map(renderMeetingCard).join('')}
    </div>
    ` : ''}

    ${completed.length > 0 ? `
    <div style="padding:16px">
      <div style="font-size:11px;letter-spacing:0.5px;color:#64748b;font-weight:600;margin-bottom:8px">✅ GEÇMİŞ (son 5)</div>
      ${completed.map(renderMeetingCard).join('')}
    </div>
    ` : ''}

    ${_meetings.length === 0 ? `
    <div style="padding:48px;text-align:center;color:var(--muted)">
      <div style="font-size:48px;margin-bottom:12px">📞</div>
      <div style="font-size:16px;margin-bottom:4px">Henüz toplantı yok</div>
      <div style="font-size:13px">Kriz durumunda hızlı karar için "+ Acil Toplantı Başlat" butonuna bas</div>
    </div>
    ` : ''}
  `;

  bindMeetingsHandlers(root);
}

// TRT (Europe/Istanbul) saat gösterimi — VPS UTC ama Yılmaz İstanbul'da.
function fmtTRT(iso: string): string {
  try {
    return new Date(iso).toLocaleString('tr-TR', {
      timeZone: 'Europe/Istanbul',
      year: '2-digit', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

function renderMeetingCard(m: Meeting): string {
  const typeLabel = TYPE_LABEL[m.type] || m.type;
  const statusLabel = STATUS_LABEL[m.status] || m.status;
  const statusColor = STATUS_COLOR[m.status] || '#94a3b8';
  const startDate = m.actual_start ? new Date(m.actual_start) : new Date(m.scheduled_start);
  const elapsedMins = m.actual_start && !m.ended_at ? Math.floor((Date.now() - startDate.getTime()) / 60000) : null;
  const startTRT = fmtTRT(m.actual_start || m.scheduled_start);
  return `
    <div class="meeting-card" data-meeting-id="${escAttr(m.id)}" style="display:flex;align-items:center;gap:12px;padding:12px;background:var(--surface-elevated);border:1px solid var(--border);border-radius:6px;margin-bottom:8px;cursor:pointer">
      <span style="font-size:14px;font-weight:600;color:${statusColor}">${typeLabel}</span>
      <span style="flex:1;color:var(--text)">${escHtml(m.title)}</span>
      <span style="font-size:11px;color:var(--muted)" title="TRT (Europe/Istanbul)">🕐 ${startTRT}</span>
      <span style="font-size:11px;color:var(--muted)">${m.participant_count || 0} kişi · ${m.decision_count || 0} karar</span>
      ${elapsedMins !== null ? `<span style="font-size:11px;color:#f59e0b">⏱ ${elapsedMins}dk</span>` : ''}
      <span style="padding:2px 8px;border-radius:4px;background:${statusColor}22;color:${statusColor};font-size:11px;font-weight:600">${statusLabel}</span>
    </div>
  `;
}

function bindMeetingsHandlers(root: HTMLElement): void {
  const newBtn = root.querySelector<HTMLButtonElement>('#meetings-new-btn');
  const refreshBtn = root.querySelector<HTMLButtonElement>('#meetings-refresh-btn');
  if (newBtn) newBtn.addEventListener('click', openNewMeetingModal);
  if (refreshBtn) refreshBtn.addEventListener('click', () => loadMeetings());
  root.querySelectorAll<HTMLElement>('.meeting-card').forEach(card => {
    card.addEventListener('click', () => {
      const id = card.getAttribute('data-meeting-id');
      if (id) openMeetingDetail(id);
    });
  });
}

function openNewMeetingModal(): void {
  // Modal HTML inject (idempotent)
  let modal = document.getElementById('meeting-new-modal');
  if (modal) modal.remove();
  modal = document.createElement('div');
  modal.id = 'meeting-new-modal';
  modal.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.6);z-index:9999;display:flex;align-items:center;justify-content:center';
  modal.innerHTML = `
    <div style="background:var(--surface-elevated);border:1px solid var(--border);border-radius:8px;padding:24px;width:500px;max-width:90vw;max-height:90vh;overflow:auto">
      <h2 style="margin:0 0 16px;color:var(--text);font-size:18px">📞 Acil Toplantı Başlat</h2>

      <label style="display:block;margin-bottom:12px;color:var(--muted);font-size:12px">Tip:</label>
      <div style="display:flex;flex-direction:column;gap:6px;margin-bottom:16px">
        <label style="display:flex;gap:8px;align-items:center"><input type="radio" name="mtype" value="T3-crisis" checked> 🔴 T3 Kriz (5-15dk, hızlı veto/onay)</label>
        <label style="display:flex;gap:8px;align-items:center"><input type="radio" name="mtype" value="T2-topic"> 💬 T2 Konu (15-45dk, GM+ekip)</label>
        <label style="display:flex;gap:8px;align-items:center"><input type="radio" name="mtype" value="T1-board"> 🏛️ T1 Yönetim Kurulu (60-90dk, formal)</label>
        <label style="display:flex;gap:8px;align-items:center"><input type="radio" name="mtype" value="T4-async"> ⏳ T4 Asenkron (4-24sa, Telegram oy)</label>
      </div>

      <label style="display:block;margin-bottom:4px;color:var(--muted);font-size:12px">Konu:</label>
      <input id="mtitle" type="text" placeholder="Örnek: Mervaks tel: link bozuk" style="width:100%;padding:8px;border:1px solid var(--border);background:var(--surface);color:var(--text);border-radius:4px;margin-bottom:12px" />

      <label style="display:block;margin-bottom:4px;color:var(--muted);font-size:12px">Açıklama (opsiyonel):</label>
      <textarea id="mdesc" rows="3" placeholder="Detay: ne, niye, beklenen karar..." style="width:100%;padding:8px;border:1px solid var(--border);background:var(--surface);color:var(--text);border-radius:4px;margin-bottom:12px;font-family:inherit"></textarea>

      <label style="display:block;margin-bottom:4px;color:var(--muted);font-size:12px">Katılımcılar (virgülle ayır):</label>
      <input id="mparts" type="text" value="yilmaz,nexus" placeholder="yilmaz,nexus,sentinel,castmart-gm" style="width:100%;padding:8px;border:1px solid var(--border);background:var(--surface);color:var(--text);border-radius:4px;margin-bottom:4px;font-family:monospace;font-size:13px" />
      <div id="mparts-hint" style="font-size:11px;color:var(--muted);margin-bottom:12px">💡 Tip seçtiğinde otomatik doldurulur (override edebilirsin).</div>

      <label style="display:block;margin-bottom:4px;color:var(--muted);font-size:12px">Hedef süre (dk):</label>
      <input id="mduration" type="number" value="15" min="5" max="1440" style="width:100px;padding:8px;border:1px solid var(--border);background:var(--surface);color:var(--text);border-radius:4px;margin-bottom:16px" />

      <div style="display:flex;gap:8px;justify-content:flex-end">
        <button id="mcancel" class="btn btn-secondary" style="padding:8px 16px">İptal</button>
        <button id="mcreate" class="btn btn-primary" style="padding:8px 16px;font-weight:600">Başlat</button>
      </div>
    </div>
  `;
  document.body.appendChild(modal);

  // CHARTER v1.3 § 2.1 — tipe göre otomatik katılımcı + süre defaults
  const TYPE_DEFAULTS: Record<string, { participants: string; duration: number; chair: string }> = {
    'T1-board':  { participants: 'yilmaz,nexus,sentinel,castmart-gm,iq7s-gm,castacc-gm', duration: 60,   chair: 'yilmaz' },
    'T2-topic':  { participants: 'yilmaz,nexus',                                          duration: 30,   chair: 'yilmaz' },
    'T3-crisis': { participants: 'yilmaz,nexus',                                          duration: 15,   chair: 'yilmaz' },
    'T4-async':  { participants: 'yilmaz,nexus,sentinel,castmart-gm,iq7s-gm,castacc-gm', duration: 1440, chair: 'yilmaz' },
  };
  // Auto-fill on type change
  const partsInput = modal.querySelector<HTMLInputElement>('#mparts');
  const durInput = modal.querySelector<HTMLInputElement>('#mduration');
  const hintEl = modal.querySelector<HTMLDivElement>('#mparts-hint');
  modal.querySelectorAll<HTMLInputElement>('input[name="mtype"]').forEach(radio => {
    radio.addEventListener('change', () => {
      const def = TYPE_DEFAULTS[radio.value];
      if (!def || !partsInput || !durInput) return;
      partsInput.value = def.participants;
      durInput.value = String(def.duration);
      if (hintEl) {
        const labels: Record<string, string> = {
          'T1-board': '🏛️ Yönetim Kurulu: 6 Board üyesi (CHARTER § 2.1) · oy: çoğunluk + Yılmaz veto',
          'T2-topic': '💬 Konu: Yılmaz + Nexus (ilgili GM/ekip ekle)',
          'T3-crisis': '🔴 Kriz: Yılmaz + Nexus (5dk auto-proceed)',
          'T4-async': '⏳ Async Mini-Board: 6 üye (24sa Telegram oy)',
        };
        hintEl.textContent = labels[radio.value] || '';
      }
    });
  });
  // İlk default (T3 default)
  modal.querySelector<HTMLInputElement>('input[name="mtype"]:checked')?.dispatchEvent(new Event('change'));

  const cancelBtn = modal.querySelector<HTMLButtonElement>('#mcancel');
  const createBtn = modal.querySelector<HTMLButtonElement>('#mcreate');
  if (cancelBtn) cancelBtn.addEventListener('click', () => modal!.remove());
  if (createBtn) createBtn.addEventListener('click', async () => {
    const type = (modal!.querySelector<HTMLInputElement>('input[name="mtype"]:checked')?.value || 'T3-crisis');
    const title = modal!.querySelector<HTMLInputElement>('#mtitle')?.value.trim() || '';
    const desc = modal!.querySelector<HTMLTextAreaElement>('#mdesc')?.value.trim() || '';
    const parts = modal!.querySelector<HTMLInputElement>('#mparts')?.value.split(',').map(s => s.trim()).filter(Boolean) || [];
    const duration = parseInt(modal!.querySelector<HTMLInputElement>('#mduration')?.value || '15', 10);
    if (title.length < 3) { toast('Konu en az 3 karakter olmalı', 'error'); return; }
    if (parts.length === 0) { toast('En az 1 katılımcı', 'error'); return; }
    createBtn.disabled = true;
    createBtn.textContent = 'Başlatılıyor...';
    try {
      const r = await apiFetch('/castintech-api/meetings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type, title, description: desc,
          participants: parts.map(name => ({ agentName: name, role: name === 'yilmaz' ? 'chair' : 'voter' })),
          targetDurationMins: duration,
          initiatedBy: 'yilmaz',
        }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        toast(`Hata: ${err.message || r.status}`, 'error');
        createBtn.disabled = false;
        createBtn.textContent = 'Başlat';
        return;
      }
      const d = await r.json() as { meeting: Meeting };
      // Auto-start
      await apiFetch(`/castintech-api/meetings/${d.meeting.id}/start`, { method: 'POST' });
      toast(`✓ Toplantı başlatıldı: ${title}`, 'success');
      modal!.remove();
      await loadMeetings();
    } catch (e) {
      toast(`Hata: ${(e as Error).message}`, 'error');
      createBtn.disabled = false;
      createBtn.textContent = 'Başlat';
    }
  });

  // ESC ile kapat
  document.addEventListener('keydown', function escHandler(e: KeyboardEvent) {
    if (e.key === 'Escape') { modal!.remove(); document.removeEventListener('keydown', escHandler); }
  });
}

// V2 — Active Meeting Drawer (chat + gündem + katılımcılar)
let _drawerMeetingId: string | null = null;
let _drawerPollTimer: number | null = null;

interface AgendaItem {
  id: string;
  meeting_id: string;
  title: string;
  description?: string;
  ord: number;
  status: 'pending' | 'in_discussion' | 'done' | 'parked';
  added_by: string;
}
interface MeetingMessage {
  id: string;
  meeting_id: string;
  author: string;
  body: string;
  type: 'chat' | 'question' | 'answer' | 'system' | 'proposal-link' | 'skip';
  parent_id?: string;
  created_at: string;
}

async function openMeetingDetail(id: string): Promise<void> {
  // Mevcut drawer varsa kapat
  closeMeetingDrawer();

  const drawer = document.createElement('div');
  drawer.id = 'meeting-drawer';
  drawer.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.85);z-index:9998;display:flex;align-items:center;justify-content:center';
  drawer.innerHTML = `
    <div style="background:var(--surface-elevated);border:1px solid var(--border);border-radius:8px;width:95vw;max-width:1400px;height:90vh;display:flex;flex-direction:column;overflow:hidden">
      <div id="md-header" style="padding:12px 16px;border-bottom:1px solid var(--border);background:var(--surface);display:flex;align-items:center;gap:12px">
        <div style="flex:1">
          <div id="md-title" style="font-size:16px;font-weight:600;color:var(--text)">Toplantı yükleniyor…</div>
          <div id="md-sub" style="font-size:11px;color:var(--muted)">…</div>
        </div>
        <button id="md-propose" class="btn btn-primary" style="padding:6px 14px;font-size:13px">+ Karar Öner</button>
        <button id="md-end" class="btn btn-secondary" style="padding:6px 14px;font-size:13px;background:#ef4444;color:#fff;border:none">⏹ Bitir</button>
        <button id="md-close" class="btn btn-secondary" style="padding:6px 14px;font-size:13px">✕ Kapat</button>
      </div>
      <div style="flex:1;display:grid;grid-template-columns:280px 1fr 240px;overflow:hidden">
        <!-- Sol: Gündem -->
        <div id="md-agenda" style="border-right:1px solid var(--border);overflow-y:auto;padding:12px;background:var(--surface)">
          <div style="font-size:11px;letter-spacing:0.5px;color:var(--muted);font-weight:600;margin-bottom:8px">📋 GÜNDEM</div>
          <div id="md-agenda-list" style="display:flex;flex-direction:column;gap:6px"></div>
          <div style="display:flex;gap:4px;margin-top:12px">
            <input id="md-agenda-input" type="text" placeholder="Yeni gündem..." style="flex:1;padding:6px;font-size:12px;border:1px solid var(--border);background:var(--surface-elevated);color:var(--text);border-radius:4px" />
            <button id="md-agenda-add" style="padding:6px 10px;font-size:12px;background:var(--accent);color:#fff;border:none;border-radius:4px;cursor:pointer">+</button>
          </div>
        </div>
        <!-- Orta: Chat -->
        <div style="display:flex;flex-direction:column;overflow:hidden">
          <div id="md-chat" style="flex:1;overflow-y:auto;padding:12px;background:var(--surface-elevated)"></div>
          <div style="border-top:1px solid var(--border);padding:8px;display:flex;gap:6px;background:var(--surface)">
            <select id="md-msg-type" style="padding:6px;font-size:12px;border:1px solid var(--border);background:var(--surface-elevated);color:var(--text);border-radius:4px">
              <option value="chat">💬 Yorum</option>
              <option value="question">❓ Soru</option>
              <option value="answer">💡 Cevap</option>
            </select>
            <input id="md-msg-input" type="text" placeholder="Mesaj yaz... (Enter)" style="flex:1;padding:6px 10px;font-size:13px;border:1px solid var(--border);background:var(--surface-elevated);color:var(--text);border-radius:4px" />
            <button id="md-msg-send" style="padding:6px 14px;font-size:13px;background:var(--accent);color:#fff;border:none;border-radius:4px;cursor:pointer;font-weight:600">Gönder</button>
          </div>
        </div>
        <!-- Sağ: Katılımcılar + Kararlar -->
        <div style="border-left:1px solid var(--border);overflow-y:auto;padding:12px;background:var(--surface);font-size:12px">
          <div style="font-size:11px;letter-spacing:0.5px;color:var(--muted);font-weight:600;margin-bottom:8px">👥 KATILIMCILAR</div>
          <div id="md-participants" style="display:flex;flex-direction:column;gap:4px;margin-bottom:16px"></div>
          <div style="font-size:11px;letter-spacing:0.5px;color:var(--muted);font-weight:600;margin-bottom:8px">📋 KARARLAR</div>
          <div id="md-decisions" style="display:flex;flex-direction:column;gap:6px"></div>
        </div>
      </div>
    </div>
  `;
  document.body.appendChild(drawer);
  _drawerMeetingId = id;

  // Event listeners
  drawer.querySelector<HTMLButtonElement>('#md-close')?.addEventListener('click', closeMeetingDrawer);
  drawer.querySelector<HTMLButtonElement>('#md-end')?.addEventListener('click', () => endMeeting(id));
  drawer.querySelector<HTMLButtonElement>('#md-propose')?.addEventListener('click', () => openProposeDecisionModal(id));
  drawer.querySelector<HTMLButtonElement>('#md-agenda-add')?.addEventListener('click', () => addAgendaItem(id));
  drawer.querySelector<HTMLInputElement>('#md-agenda-input')?.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.key === 'Enter') { e.preventDefault(); addAgendaItem(id); }
  });
  drawer.querySelector<HTMLButtonElement>('#md-msg-send')?.addEventListener('click', () => sendMeetingMessage(id));
  drawer.querySelector<HTMLInputElement>('#md-msg-input')?.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.key === 'Enter') { e.preventDefault(); sendMeetingMessage(id); }
  });

  // İlk yükleme
  await refreshDrawer(id);
  // 3sn poll (drawer açıkken)
  _drawerPollTimer = window.setInterval(() => { if (_drawerMeetingId === id) void refreshDrawer(id); }, 3000);
}

function closeMeetingDrawer(): void {
  if (_drawerPollTimer !== null) { window.clearInterval(_drawerPollTimer); _drawerPollTimer = null; }
  _drawerMeetingId = null;
  const drawer = document.getElementById('meeting-drawer');
  if (drawer) drawer.remove();
}

async function refreshDrawer(id: string): Promise<void> {
  try {
    const [detailRes, msgRes, agendaRes] = await Promise.all([
      apiFetch(`/castintech-api/meetings/${id}`),
      apiFetch(`/castintech-api/meetings/${id}/messages`),
      apiFetch(`/castintech-api/meetings/${id}/agenda`),
    ]);
    if (!detailRes.ok) return;
    const detail = await detailRes.json() as { meeting: Meeting; participants: { agent_name: string; role: string }[]; decisions: { id: string; proposal_text: string; outcome?: string; proposed_by: string; votes?: { voter: string; vote: string }[] }[] };
    const messages: MeetingMessage[] = msgRes.ok ? (await msgRes.json()).messages : [];
    const agenda: AgendaItem[] = agendaRes.ok ? (await agendaRes.json()).items : [];

    // Header
    const title = $('md-title'); const sub = $('md-sub');
    if (title) title.textContent = `${TYPE_LABEL[detail.meeting.type] || detail.meeting.type} — ${detail.meeting.title}`;
    if (sub) sub.textContent = `Status: ${STATUS_LABEL[detail.meeting.status]} · Başlatan: ${detail.meeting.initiated_by} · ${fmtTRT(detail.meeting.actual_start || detail.meeting.scheduled_start)}`;

    // Gündem
    const agendaList = $('md-agenda-list');
    if (agendaList) {
      const STATUS_EMOJI: Record<string, string> = { pending: '⏳', in_discussion: '💭', done: '✅', parked: '⏸' };
      agendaList.innerHTML = agenda.length === 0
        ? '<div style="font-size:11px;color:var(--muted);padding:8px">Henüz gündem yok</div>'
        : agenda.map(a => `
            <div data-aid="${escAttr(a.id)}" style="padding:8px;background:var(--surface-elevated);border:1px solid var(--border);border-radius:4px;font-size:12px;display:flex;align-items:center;gap:6px">
              <span style="cursor:pointer" data-aid="${escAttr(a.id)}" data-status="${a.status}">${STATUS_EMOJI[a.status] || '⏳'}</span>
              <span style="flex:1;color:var(--text)${a.status === 'done' ? ';text-decoration:line-through;opacity:0.6' : ''}">${escHtml(a.title)}</span>
            </div>
          `).join('');
      agendaList.querySelectorAll<HTMLElement>('[data-aid][data-status]').forEach(el => {
        el.addEventListener('click', async () => {
          const aid = el.getAttribute('data-aid'); const cur = el.getAttribute('data-status');
          if (!aid || !cur) return;
          const next = cur === 'pending' ? 'in_discussion' : cur === 'in_discussion' ? 'done' : cur === 'done' ? 'parked' : 'pending';
          await apiFetch(`/castintech-api/meetings/${id}/agenda/${aid}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status: next }) });
          await refreshDrawer(id);
        });
      });
    }

    // Katılımcılar — durum dot v2 (2026-05-28 — ajan SON cevabı bazlı, kümülatif değil)
    // Sorun: eski logic "Yılmaz son mesajından sonra cevap?" → ajan 30 dk geç cevap verirse
    //   bir daha asla yeşil olmuyordu. Yeni mantık: ajanın EN SON mesajı + Yılmaz timing'i:
    //   - Ajan EN SON mesajı Yılmaz son mesajından SONRA → ✓ yeşil (cevap verdi)
    //   - Ajan EN SON mesajı Yılmaz son mesajından ÖNCE + <5dk → düşünüyor
    //   - 5+ dk hâlâ cevap yok → gecikti (kırmızı)
    //   - Hiç mesajı yok → boşta
    const partsEl = $('md-participants');
    if (partsEl) {
      const ROLE_EMOJI: Record<string, string> = { chair: '👑', voter: '🗳️', observer: '👁', expert: '🎯' };
      const lastYilmaz = messages.filter(m => m.author === 'yilmaz').pop();
      const yilmazTs = lastYilmaz ? new Date(lastYilmaz.created_at).getTime() : 0;
      // Her ajanın EN SON mesajı (Yılmaz timing'den bağımsız)
      const agentLastMsg: Record<string, MeetingMessage | undefined> = {};
      for (const m of messages) {
        if (m.author !== 'yilmaz') agentLastMsg[m.author] = m;
      }
      const nowMs = Date.now();
      const THINKING_WINDOW_SEC = 300; // 3dk → 5dk: ajan claude run uzun sürebilir

      partsEl.innerHTML = detail.participants.map(p => {
        let statusDot = '<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:#64748b" title="boşta — henüz konuşmadı"></span>';
        let statusText = '';
        if (p.agent_name === 'yilmaz') {
          statusDot = '<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:#22c55e" title="aktif"></span>';
        } else {
          const lastMsg = agentLastMsg[p.agent_name];
          if (lastMsg) {
            const lastTs = new Date(lastMsg.created_at).getTime();
            const secAgo = Math.floor((nowMs - lastTs) / 1000);
            const lastAgoStr = secAgo < 60 ? secAgo + 's' : (secAgo < 3600 ? Math.floor(secAgo / 60) + 'dk' : Math.floor(secAgo / 3600) + 'sa');

            if (!yilmazTs || lastTs > yilmazTs) {
              // Ajan'ın son mesajı Yılmaz son mesajından SONRA gelmiş → güncel cevap
              statusDot = '<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:#22c55e" title="güncel cevap"></span>';
              statusText = `<span style="font-size:9px;color:#22c55e">✓ ${lastAgoStr}</span>`;
            } else {
              // Yılmaz daha yeni bir mesaj atmış, ajan henüz cevap vermemiş
              const elapsedSec = Math.floor((nowMs - yilmazTs) / 1000);
              const elapsedMin = Math.floor(elapsedSec / 60);
              if (elapsedSec < THINKING_WINDOW_SEC) {
                statusDot = '<span class="meeting-thinking-dot" style="display:inline-block;width:8px;height:8px;border-radius:50%;background:#f59e0b;animation:pulse 1.2s ease-in-out infinite" title="düşünüyor — Yılmaz son mesajına cevap bekleniyor"></span>';
                statusText = `<span style="font-size:9px;color:#f59e0b">💭 ${elapsedSec < 60 ? elapsedSec + 's' : elapsedMin + 'dk'}</span>`;
              } else {
                // Gecikti — ama önceki cevabını da göster
                statusDot = '<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:#ef4444" title="cevap gecikti — önceki: ' + lastAgoStr + ' önce"></span>';
                statusText = `<span style="font-size:9px;color:#ef4444">⚠ ${elapsedMin}dk</span>`;
              }
            }
          } else if (yilmazTs > 0) {
            // Ajan hiç mesaj yazmadı + Yılmaz mesaj atmış → düşünüyor (ilk cevap bekleniyor)
            const elapsedSec = Math.floor((nowMs - yilmazTs) / 1000);
            const elapsedMin = Math.floor(elapsedSec / 60);
            if (elapsedSec < THINKING_WINDOW_SEC) {
              statusDot = '<span class="meeting-thinking-dot" style="display:inline-block;width:8px;height:8px;border-radius:50%;background:#f59e0b;animation:pulse 1.2s ease-in-out infinite" title="düşünüyor"></span>';
              statusText = `<span style="font-size:9px;color:#f59e0b">💭 ${elapsedSec < 60 ? elapsedSec + 's' : elapsedMin + 'dk'}</span>`;
            } else {
              statusDot = '<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:#ef4444" title="hiç cevap yok"></span>';
              statusText = `<span style="font-size:9px;color:#ef4444">⚠ ${elapsedMin}dk</span>`;
            }
          }
        }
        return `
          <div style="display:flex;align-items:center;gap:6px;padding:4px 6px;background:var(--surface-elevated);border-radius:4px">
            ${statusDot}
            <span>${ROLE_EMOJI[p.role] || '👤'}</span>
            <span style="flex:1;color:var(--text);font-size:12px">${escHtml(p.agent_name)}</span>
            ${statusText}
          </div>
        `;
      }).join('');
      // Pulse keyframes CSS inject (idempotent)
      if (!document.getElementById('meeting-pulse-css')) {
        const style = document.createElement('style');
        style.id = 'meeting-pulse-css';
        style.textContent = '@keyframes pulse { 0%,100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.4; transform: scale(1.4); } }';
        document.head.appendChild(style);
      }
    }

    // Chat — kurumsal kart tasarımı (css/meeting-chat.css)
    // Her ajan kendi gem-color'ı ile (avatar daire + sol kenar şerit)
    // Yılmaz CEO: chair stripe (üst kenar indigo). System: merkez italik.
    // user-select:text + pre-wrap → kopyala-yapıştır + format korunur.
    const chatEl = $('md-chat');
    if (chatEl) {
      const wasNearBottom = chatEl.scrollTop + chatEl.clientHeight >= chatEl.scrollHeight - 50;
      const TYPE_LABEL: Record<string, string> = {
        question: 'soru', answer: 'cevap', system: 'sistem', 'proposal-link': 'öneri', skip: 'ek bilgi yok',
      };
      const TYPE_CLS: Record<string, string> = {
        question: 'question', answer: 'answer', system: 'system', 'proposal-link': 'proposal', skip: 'skip',
      };

      if (messages.length === 0) {
        chatEl.innerHTML = '<div class="mtg-msg-empty">Henüz konuşma yok — ilk mesajı sen yaz</div>';
      } else {
        chatEl.innerHTML = '<div class="mtg-msg-list">' + messages.map(m => {
          const isChair = m.author === 'yilmaz';
          const isSystem = m.type === 'system' || m.author === 'system';
          // V4 (2026-05-28): skip mesajları — ajan "ek bilgi yok" cevabı; type='skip'
          // bridge tarafından inject edilir. Kompakt + gri stille render.
          const isSkip = m.type === 'skip';
          const gem = getGem(m.author);
          const initial = (m.author || '?').charAt(0).toUpperCase();
          const typeBadge = (m.type && m.type !== 'chat' && !isSystem)
            ? `<span class="mtg-msg-type mtg-msg-type--${TYPE_CLS[m.type] || 'system'}">${TYPE_LABEL[m.type] || m.type}</span>`
            : '';
          const cardCls = `mtg-msg ${gem.cls}${isChair ? ' mtg-msg--chair' : ''}${isSystem ? ' mtg-msg--system' : ''}${isSkip ? ' mtg-msg--skip' : ''}`;
          const authorCls = `mtg-msg-author${isChair ? ' mtg-msg-author--chair' : ''}`;
          // Yılmaz için chair badge (CEO), diğerleri için ajan rol
          const roleBadge = isChair
            ? '<span class="mtg-msg-role" title="Chairman / CEO">CEO</span>'
            : '';

          return `
            <article class="${cardCls}">
              <div class="mtg-msg-avatar" data-gem-icon="${escAttr(gem.icon || '')}" aria-hidden="true">${escHtml(initial)}</div>
              <div class="mtg-msg-content">
                <header class="mtg-msg-header">
                  <span class="${authorCls}">${escHtml(m.author)}</span>
                  ${roleBadge}
                  ${typeBadge}
                  <time class="mtg-msg-time" datetime="${escAttr(m.created_at)}">${fmtTRT(m.created_at)}</time>
                </header>
                <div class="mtg-msg-body">${escHtml(m.body)}</div>
              </div>
            </article>`;
        }).join('') + '</div>';
      }
      if (wasNearBottom) chatEl.scrollTop = chatEl.scrollHeight;
    }

    // Kararlar
    const decEl = $('md-decisions');
    if (decEl) {
      decEl.innerHTML = detail.decisions.length === 0
        ? '<div style="font-size:11px;color:var(--muted);padding:8px">Henüz karar yok</div>'
        : detail.decisions.map(d => {
            const outcomeColor: Record<string, string> = { approved: '#22c55e', rejected: '#ef4444', vetoed: '#dc2626', tied: '#f59e0b', timeout: '#94a3b8' };
            const oc = d.outcome ? `<span style="padding:1px 6px;border-radius:3px;background:${outcomeColor[d.outcome] || '#94a3b8'}22;color:${outcomeColor[d.outcome] || '#94a3b8'};font-size:10px;font-weight:600">${d.outcome}</span>` : '<span style="color:#f59e0b;font-size:10px">⏳ oy bekliyor</span>';
            const votesHtml = !d.outcome ? `
              <div style="display:flex;gap:4px;margin-top:4px">
                <button data-did="${escAttr(d.id)}" data-vote="yes"     style="padding:2px 8px;background:#22c55e;color:#fff;border:none;border-radius:3px;cursor:pointer;font-size:11px">👍</button>
                <button data-did="${escAttr(d.id)}" data-vote="no"      style="padding:2px 8px;background:#ef4444;color:#fff;border:none;border-radius:3px;cursor:pointer;font-size:11px">👎</button>
                <button data-did="${escAttr(d.id)}" data-vote="abstain" style="padding:2px 8px;background:#64748b;color:#fff;border:none;border-radius:3px;cursor:pointer;font-size:11px">⏭</button>
              </div>` : '';
            const voteCount = d.votes ? `<div style="font-size:10px;color:var(--muted);margin-top:2px">${d.votes.length} oy</div>` : '';
            return `
              <div style="padding:6px;background:var(--surface-elevated);border:1px solid var(--border);border-radius:4px;font-size:11px">
                <div style="color:var(--text);margin-bottom:2px">${escHtml(d.proposal_text.substring(0, 80))}${d.proposal_text.length > 80 ? '…' : ''}</div>
                ${oc}
                ${voteCount}
                ${votesHtml}
              </div>`;
          }).join('');
      decEl.querySelectorAll<HTMLButtonElement>('button[data-did]').forEach(btn => {
        btn.addEventListener('click', async () => {
          const did = btn.getAttribute('data-did'); const vote = btn.getAttribute('data-vote');
          if (!did || !vote) return;
          await apiFetch(`/castintech-api/meetings/${id}/vote`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ decisionId: did, voterName: 'yilmaz', vote }),
          });
          toast(`✓ Oy verildi: ${vote}`, 'success');
          await refreshDrawer(id);
        });
      });
    }
  } catch (e) {
    console.warn('[meeting drawer refresh]', (e as Error).message);
  }
}

async function sendMeetingMessage(id: string): Promise<void> {
  const input = $('md-msg-input') as HTMLInputElement | null;
  const typeSel = $('md-msg-type') as HTMLSelectElement | null;
  if (!input || !input.value.trim()) return;
  try {
    await apiFetch(`/castintech-api/meetings/${id}/messages`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ author: 'yilmaz', body: input.value.trim(), type: typeSel?.value || 'chat' }),
    });
    input.value = '';
    await refreshDrawer(id);
  } catch (e) {
    toast(`Hata: ${(e as Error).message}`, 'error');
  }
}

async function addAgendaItem(id: string): Promise<void> {
  const input = $('md-agenda-input') as HTMLInputElement | null;
  if (!input || !input.value.trim()) return;
  try {
    await apiFetch(`/castintech-api/meetings/${id}/agenda`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title: input.value.trim(), addedBy: 'yilmaz' }),
    });
    input.value = '';
    await refreshDrawer(id);
  } catch (e) {
    toast(`Hata: ${(e as Error).message}`, 'error');
  }
}

function openProposeDecisionModal(meetingId: string): void {
  let modal = document.getElementById('propose-decision-modal');
  if (modal) modal.remove();
  modal = document.createElement('div');
  modal.id = 'propose-decision-modal';
  modal.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.7);z-index:9999;display:flex;align-items:center;justify-content:center';
  modal.innerHTML = `
    <div style="background:var(--surface-elevated);border:1px solid var(--border);border-radius:8px;padding:20px;width:480px;max-width:90vw">
      <h3 style="margin:0 0 12px;font-size:16px;color:var(--text)">📋 Karar Öner</h3>
      <label style="display:block;font-size:12px;color:var(--muted);margin-bottom:4px">Karar metni:</label>
      <textarea id="pd-text" rows="3" placeholder="Örnek: Cuma Board paketinde Nexus basitleştirme paketi onaylanır" style="width:100%;padding:8px;border:1px solid var(--border);background:var(--surface);color:var(--text);border-radius:4px;margin-bottom:12px;font-family:inherit;font-size:13px"></textarea>
      <label style="display:block;font-size:12px;color:var(--muted);margin-bottom:4px">EFQM Kriteri:</label>
      <select id="pd-efqm" style="width:100%;padding:8px;border:1px solid var(--border);background:var(--surface);color:var(--text);border-radius:4px;margin-bottom:12px;font-size:13px">
        <option value="">— seç —</option>
        <option value="1">1 — Liderlik + Kültür</option>
        <option value="2">2 — Strateji + Kalite</option>
        <option value="3">3 — Paydaş Yönetimi</option>
        <option value="4">4 — Süreç + Dönüşüm</option>
        <option value="5">5 — Sonuç + Performans</option>
      </select>
      <label style="display:block;font-size:12px;color:var(--muted);margin-bottom:4px">Atama (opsiyonel — approved sonrası Paperclip dispatch):</label>
      <input id="pd-assignee" type="text" placeholder="örn: nexus" style="width:100%;padding:8px;border:1px solid var(--border);background:var(--surface);color:var(--text);border-radius:4px;margin-bottom:12px;font-size:13px" />
      <label style="display:block;font-size:12px;color:var(--muted);margin-bottom:4px">Oy penceresi (dk):</label>
      <input id="pd-window" type="number" value="5" min="1" max="60" style="width:80px;padding:8px;border:1px solid var(--border);background:var(--surface);color:var(--text);border-radius:4px;margin-bottom:16px" />
      <div style="display:flex;gap:8px;justify-content:flex-end">
        <button id="pd-cancel" class="btn btn-secondary" style="padding:8px 16px">İptal</button>
        <button id="pd-submit" class="btn btn-primary" style="padding:8px 16px;font-weight:600">Karara Sun</button>
      </div>
    </div>
  `;
  document.body.appendChild(modal);
  modal.querySelector<HTMLButtonElement>('#pd-cancel')?.addEventListener('click', () => modal!.remove());
  modal.querySelector<HTMLButtonElement>('#pd-submit')?.addEventListener('click', async () => {
    const text = modal!.querySelector<HTMLTextAreaElement>('#pd-text')?.value.trim() || '';
    const efqm = modal!.querySelector<HTMLSelectElement>('#pd-efqm')?.value || '';
    const assignee = modal!.querySelector<HTMLInputElement>('#pd-assignee')?.value.trim() || '';
    const winMins = parseInt(modal!.querySelector<HTMLInputElement>('#pd-window')?.value || '5', 10);
    if (text.length < 5) { toast('Karar metni en az 5 karakter olmalı', 'error'); return; }
    try {
      await apiFetch(`/castintech-api/meetings/${meetingId}/decision`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ proposalText: text, proposedBy: 'yilmaz', voteWindowMins: winMins, efqmKriteri: efqm ? parseInt(efqm, 10) : null, dispatchAssignee: assignee || null }),
      });
      toast('✓ Karar oya sunuldu', 'success');
      modal!.remove();
      await refreshDrawer(meetingId);
    } catch (e) {
      toast(`Hata: ${(e as Error).message}`, 'error');
    }
  });
}

async function endMeeting(id: string): Promise<void> {
  if (!confirm('Toplantıyı bitir — bekleyen oylar timeout/sayım ile kapanır. Onaylıyor musun?')) return;
  try {
    const r = await apiFetch(`/castintech-api/meetings/${id}/end`, { method: 'POST' });
    if (!r.ok) { toast('Bitirme hatası: HTTP ' + r.status, 'error'); return; }
    toast('✓ Toplantı sona erdi — tutanak hazırlanıyor', 'success');
    closeMeetingDrawer();
    await loadMeetings();
  } catch (e) {
    toast(`Hata: ${(e as Error).message}`, 'error');
  }
}

export function startMeetingsPolling(): void {
  if (_pollTimer !== null) return;
  _pollTimer = window.setInterval(() => {
    if (state.currentPage === 'meetings' || _meetings.some(m => m.status === 'active' || m.status === 'voting')) {
      loadMeetings();
    }
  }, 5000);
}

export function stopMeetingsPolling(): void {
  if (_pollTimer !== null) { window.clearInterval(_pollTimer); _pollTimer = null; }
}
